/**
 * useWizardMachine Hook
 *
 * Simplified hook that wraps the XState wizard machine.
 *
 * Key difference from previous implementation:
 * - NO useEffects for state synchronization
 * - Machine controls all async operations (fetch, poll)
 * - Data comes from machine context, not external ViewModel
 * - Just reads from machine state and exposes actions
 */

import { useMemo, useCallback } from 'react'
import { useMachine } from '@xstate/react'
import { wizardMachine } from './wizardMachine'
import {
  WizardStep,
  ProcessType,
  InternalState,
  NestedState,
  EventType,
  getProcessMessage,
} from './types'
import { ImportData } from '../importer-types'

export function useWizardMachine(importNumber: number) {
  const [state, send] = useMachine(wizardMachine, {
    input: { importNumber },
  })

  // ============ Derived Values from State ============

  const currentStep = useMemo((): WizardStep | null => {
    if (state.matches(WizardStep.STEP1_CONFIRM)) return WizardStep.STEP1_CONFIRM
    if (state.matches(WizardStep.STEP2_REVIEW)) return WizardStep.STEP2_REVIEW
    if (state.matches(WizardStep.STEP3_DECIDE)) return WizardStep.STEP3_DECIDE
    if (state.matches(WizardStep.STEP4_IMPORT)) return WizardStep.STEP4_IMPORT
    return null
  }, [state])

  const canGoBack = useMemo(() => {
    if (!currentStep) return false
    const availableSteps = state.context.availableSteps
    const index = availableSteps.indexOf(currentStep)
    return index > 0
  }, [currentStep, state.context.availableSteps])

  const canGoNext = useMemo(() => {
    if (!currentStep) return false
    const availableSteps = state.context.availableSteps
    const index = availableSteps.indexOf(currentStep)
    return index < availableSteps.length - 1
  }, [currentStep, state.context.availableSteps])

  // Loading states - all derived from machine state, no effects!
  const isInitializing = state.matches(InternalState.INITIALIZING)
  const isWaitingForProcess = state.matches(InternalState.WAITING_FOR_PROCESS)
  const isRefreshing = state.matches(InternalState.REFRESHING)
  const isProcessing = state.matches(InternalState.PROCESSING)
  const isSaving = state.matches(InternalState.SAVING)
  const isError = state.matches(InternalState.ERROR)

  const isLoading = isInitializing || isRefreshing || isSaving
  const isDeselectModalOpen = state.matches({
    [WizardStep.STEP1_CONFIRM]: NestedState.DESELECT_MODAL,
  })
  const isConfirmModalOpen = state.matches({
    [WizardStep.STEP3_DECIDE]: NestedState.CONFIRM_MODAL,
  })

  // Loading message
  const loadingMessage = useMemo(() => {
    if (isInitializing) return 'Loading import data...'
    if (isSaving) return 'Saving changes...'
    if (isWaitingForProcess || isProcessing) {
      return getProcessMessage(state.context.currentProcess)
    }
    if (isRefreshing) return 'Refreshing data...'
    return ''
  }, [
    isInitializing,
    isSaving,
    isWaitingForProcess,
    isProcessing,
    isRefreshing,
    state.context.currentProcess,
  ])

  // ============ Actions ============

  const goNext = useCallback(() => {
    send({ type: EventType.GO_NEXT })
  }, [send])

  const goBack = useCallback(() => {
    send({ type: EventType.GO_BACK })
  }, [send])

  // Step 1 modal actions
  const openDeselectModal = useCallback(() => {
    send({ type: EventType.OPEN_DESELECT_MODAL })
  }, [send])

  const confirmDeselect = useCallback(() => {
    send({ type: EventType.CONFIRM_DESELECT })
  }, [send])

  const cancelDeselect = useCallback(() => {
    send({ type: EventType.CANCEL_DESELECT })
  }, [send])

  // Step 3 modal actions
  const openConfirmModal = useCallback(() => {
    send({ type: EventType.OPEN_CONFIRM_MODAL })
  }, [send])

  const cancelConfirm = useCallback(() => {
    send({ type: EventType.CANCEL_CONFIRM })
  }, [send])

  // Process actions
  // The machine handles the full lifecycle: save data → call API → poll → refresh
  const startProcess = useCallback(
    (processType: ProcessType, originStep?: WizardStep, data?: ImportData) => {
      send({ type: EventType.START_PROCESS, processType, originStep, data })
    },
    [send]
  )

  // Save and reload (preserves current step)
  const saveAndReload = useCallback(
    (data: ImportData, skipMatching: boolean) => {
      if (!currentStep) return
      send({
        type: EventType.SAVE_AND_RELOAD,
        data,
        skipMatching,
        originStep: currentStep,
      })
    },
    [send, currentStep]
  )

  // Error recovery
  const retry = useCallback(() => {
    send({ type: EventType.RETRY })
  }, [send])

  return {
    // Raw XState state (for advanced usage)
    state,
    send,

    // Current step (derived from state)
    currentStep,
    availableSteps: state.context.availableSteps,
    dataVersion: state.context.dataVersion,
    currentProcess: state.context.currentProcess,
    error: state.context.error,

    // Data from machine context (previously from ViewModel)
    importData: state.context.importData,
    importName: state.context.importName,
    skipMatching: state.context.skipMatching,
    companiesPrematched: state.context.companiesPrematched,
    companiesPendingReview: state.context.companiesPendingReview,
    companiesLeftUnmatched: state.context.companiesLeftUnmatched,
    allCompanies: state.context.allCompanies,

    // Derived flags
    isInitializing,
    isWaitingForProcess,
    isRefreshing,
    isProcessing,
    isSaving,
    isLoading,
    isError,
    isDeselectModalOpen,
    isConfirmModalOpen,
    canGoBack,
    canGoNext,
    loadingMessage,

    // State matching helpers
    matches: state.matches.bind(state),

    // Navigation
    goNext,
    goBack,

    // Step 1 Modal
    openDeselectModal,
    confirmDeselect,
    cancelDeselect,

    // Step 3 Modal
    openConfirmModal,
    cancelConfirm,

    // Process
    startProcess,

    // Save and reload
    saveAndReload,

    // Error recovery
    retry,
  }
}

// Export type for consumers
export type WizardMachineReturn = ReturnType<typeof useWizardMachine>
